/* eslint-disable max-len */
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/store/app.reducer';
import { Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { debounceTime, filter, map } from 'rxjs/operators';
import * as DeviceActions from 'src/app/store/device/device.actions';
import { Command } from 'src/app/model/command';
import { TranslateService } from '@ngx-translate/core';
import { Site } from 'src/app/model/site';
import { DatePipe } from '@angular/common';
import { selectCurrentSite } from 'src/app/store/site/site.selector';
import { selectCurrentDevice, selectCurrentDeviceCommands, selectCurrentDeviceInfo, selectMtmaCommandAutomation, selectMtmaPermissions, selectMtmaPermissionsMaxData, selectMtmaPermissionsPagination, } from 'src/app/store/device/device.selector';
import { selectCurrentLang, selectIsDraftMode } from 'src/app/store/user/user.selector';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ItemsPagination, MtmaMaxData } from 'src/app/model/api/data-rest';
import { Lang } from 'src/app/model/lang';
import { TableLazyLoadEvent } from 'primeng/table';
import { pageSortChange } from 'src/app/services/table';
import { StandardMtmaLayout, StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { selectDeviceTabIndex } from 'src/app/store/home/home.selector';
import { Permission } from 'src/app/model/permission';
import { Device, MtmaIndexTab } from 'src/app/model/device';
import { EDeviceModel, isMtmacStandardModel, isMtmacUsModel } from 'src/app/conf/device-models';
import { DeviceInfo } from 'src/app/model/device-info';
import { MTMACSTD_MINFWVER_QUICKEASY_KEYPADS, MTMACUS_MINFWVER_QUICKEASY_KEYPADS } from 'src/app/conf/mtma-keypads';
import { MTMACSTD_MINFWVER_QUICKEASY_TAGS, MTMACUS_MINFWVER_QUICKEASY_TAGS } from 'src/app/conf/mtma-tags';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
	selector: 'app-mtma-manage-permissions',
	templateUrl: './mtma-manage-permissions.component.html',
	providers: [ConfirmationService, DialogService]
})

export class MtmaManagePermissionComponent implements OnInit, OnDestroy {
	site$: Observable<Site | undefined>;
	siteSub: Subscription | undefined;
	mtmaPermissions$: Observable<Permission[] | undefined>;
	mtmaPermissionsSub: Subscription | undefined;
	mtmaPermissionsPagination$: Observable<ItemsPagination | undefined>;
	mtmaPermissionsPaginationSub: Subscription | undefined;
	mtmaPermissionsMaxData$: Observable<MtmaMaxData | undefined>;
	mtmaPermissionsMaxDataSub: Subscription | undefined;
	mtmaCommandAutomation$: Observable<Command[] | undefined>;
	mtmaCommandAutomationSub: Subscription | undefined;
	currentLang$: Observable<Lang> = new Observable();
	currentLangSub: Subscription | undefined;
	tabIndex: number;
	tabIndex$: Observable<number | undefined>;
	tabIndexSub: Subscription | undefined;

	device: Device;
	device$: Observable<Device | undefined>;
	deviceSub: Subscription | undefined;

	fwVersion: string;
	deviceInfo: DeviceInfo;
	deviceInfo$: Observable<DeviceInfo | undefined>;
	deviceInfoSub: Subscription | undefined;

	searchFilter: FormControl = new FormControl('');
	filterSub: Subscription;
	filter: '';

	pagingSub: Subscription;
	pageSortSub: Subscription;
	paging: PaginationParams = {
		size: 10,
		page: 1,
		sort: "Code",
		order: 'desc',
	};
	pageSize: number = 10;
	pageSizeOptions = [10, 15, 20, 25];
	totalRecords: number;

	maxSizeMemorySub: Subscription;
	memorySize = 1;
	maxMemorySize = 1;

	permissionLoading: boolean;
	mtmaPermissions: Permission[];

	mtmaCommandAutomation?: Command[];

	commands: Command[];
	commands$: Observable<Command[] | undefined>;
	commandsSub: Subscription | undefined;

	currentSite: Site;

	confirmDeleteSub: Subscription;

	isActiveTask = false;
	dateTransform: DatePipe;

	ref: DynamicDialogRef | undefined;

	acceptButtonStyle: string;
	rejectButtonStyle: string;

	isDraftMode: boolean;
	isDraftMode$: Observable<boolean | undefined>;
	isDraftModeSub: Subscription;

	// Design
	screenWidth: number;

	constructor(
		//private dialog: MatDialog,
		private store: Store<AppState>,
		public confirmationService: ConfirmationService,
		private translate: TranslateService,
		public dialogService: DialogService,
		private route: ActivatedRoute,
		private router: Router,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.site$ = this.store.select(selectCurrentSite);
		this.mtmaPermissions$ = this.store.select(selectMtmaPermissions);
		this.mtmaPermissionsPagination$ = this.store.select(selectMtmaPermissionsPagination);
		this.mtmaPermissionsMaxData$ = this.store.select(selectMtmaPermissionsMaxData);
		this.mtmaCommandAutomation$ = this.store.select(selectMtmaCommandAutomation);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.tabIndex$ = this.store.select(selectDeviceTabIndex);
		this.device$ = this.store.select(selectCurrentDevice);
		this.commands$ = this.store.select(selectCurrentDeviceCommands);
		this.deviceInfo$ = this.store.select(selectCurrentDeviceInfo);
		this.isDraftMode$ = this.store.select(selectIsDraftMode);
	}

	ngOnInit(): void {
		const curreLang = this.translate.currentLang;
		this.dateTransform = new DatePipe(curreLang);

		this.dispatchGetPermissions();
		// this.store.dispatch(DeviceActions.getMtmaActions());

		this.subscribeAll();
		this.onResize();
	}

	subscribeAll() {
		this.filterSub = this.searchFilter.valueChanges.pipe(
			debounceTime(500),
			map(v => v.trim().toLowerCase())
		).subscribe(value => {
			this.filter = value;
			this.dispatchGetPermissions();
		}
		);
		this.mtmaPermissionsSub = this.mtmaPermissions$.subscribe(mtmaPermissions => {
			this.mtmaPermissions = mtmaPermissions;
			for (let i = 0; i < this.mtmaPermissions.length; i++) {
				const permission = this.mtmaPermissions[i];
				if ((permission.Commands !== null && permission.Commands !== undefined && permission.Commands.length > 0) &&
					(this.commands !== null && this.commands !== undefined && this.commands.length > 0)) {
					permission.Commands = permission.Commands.filter(cmd => cmd.CmdId === 2 || cmd.CmdId === 5);
					if ((permission.Commands !== null && permission.Commands !== undefined && permission.Commands.length > 0)) {
						for (let i = 0; i < permission.Commands.length; i++) {
							const command = this.commands.find(cmd => cmd.Id === permission.Commands[i].CmdId);
							if (command !== null && command !== undefined) {
								permission.Commands[i].CommandDescription = command.Description;
							}
						}
						this.mtmaPermissions[i].Commands = permission.Commands;
					}
				}
			}
			this.permissionLoading = false;
		});
		this.siteSub = this.site$.subscribe(currentSite => {
			this.currentSite = currentSite;
			this.findMyCommands();
		});
		this.pagingSub = this.mtmaPermissionsPagination$.pipe(filter(p => !!p))
			.subscribe(paging => {
				this.totalRecords = paging.TotalCount;
			}
			);
		this.maxSizeMemorySub = this.mtmaPermissionsMaxData$.pipe(filter(p => !!p))
			.subscribe(mtmaTagsMaxData => {
				this.memorySize = mtmaTagsMaxData.Current;
				this.maxMemorySize = mtmaTagsMaxData.Max;
			}
			);
		this.deviceSub = this.device$?.subscribe(
			(device) => {
				if (device !== undefined && device !== null) {
					this.device = device;
					this.findMyCommands();
				}
			}
		);
		this.deviceInfoSub = this.deviceInfo$.pipe(filter(d => !!d))
			.subscribe(currentDeviceInfo => {
				this.deviceInfo = currentDeviceInfo;
				this.fwVersion = this.deviceInfo.FwVersion;
			}
		);
		this.commandsSub = this.commands$.subscribe(
			commands => {
				this.commands = commands;
			}
		);
		this.tabIndexSub = this.tabIndex$?.subscribe(
			async (tabIndex: number) => {
				this.tabIndex = tabIndex;
				this.dispatchGetPermissions();
			}
		);
		this.isDraftModeSub = this.isDraftMode$.subscribe(
			(isDraftMode) => {
				this.isDraftMode = isDraftMode;
			}
		);
	}

	ngOnDestroy(): void {
		this.filterSub?.unsubscribe();
		this.mtmaPermissionsSub?.unsubscribe();
		this.siteSub?.unsubscribe();
		this.pagingSub?.unsubscribe();
		this.maxSizeMemorySub?.unsubscribe();
		this.mtmaCommandAutomationSub?.unsubscribe();
		this.currentLangSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.commandsSub?.unsubscribe();
		this.tabIndexSub?.unsubscribe();
		this.isDraftModeSub?.unsubscribe();
	}

	loadTags(event: TableLazyLoadEvent) {
		this.permissionLoading = true;

		this.paging = pageSortChange(this.paging, event);

		this.dispatchGetPermissions();
	}

	findMyCommands() {
		let currentDevIo = null;
		if (this.currentSite !== undefined && this.device !== undefined) {
			currentDevIo = this.currentSite.Devices.find(dev => dev.RootId === this.device.Id && dev.ModelId === EDeviceModel.MultiIO);
		}
		if (currentDevIo !== null && currentDevIo !== undefined) {
			this.store.dispatch(DeviceActions.getCommands({ device: currentDevIo }));
		}
	}

	underMinFwVerKeypads(): boolean {
		return (isMtmacStandardModel(this.device?.ModelId) && (this.fwVersion <= MTMACSTD_MINFWVER_QUICKEASY_KEYPADS) ||
			isMtmacUsModel(this.device?.ModelId) && (this.fwVersion <= MTMACUS_MINFWVER_QUICKEASY_KEYPADS)
		);
	}

	underMinFwVerTags(): boolean {
		return (isMtmacStandardModel(this.device?.ModelId) && (this.fwVersion <= MTMACSTD_MINFWVER_QUICKEASY_TAGS) ||
			isMtmacUsModel(this.device?.ModelId) && (this.fwVersion <= MTMACUS_MINFWVER_QUICKEASY_TAGS)
		);
	}

	dispatchGetPermissions(): void {
		if (this.isSelectTab()) {
			this.store.dispatch(DeviceActions.getMtmaPermissions({ filter: this.filter, sort: this.paging.sort, order: this.paging.order, size: this.paging.size, page: this.paging.page }));
		}
	}

	isSelectTab(): boolean {
		return this.tabIndex === MtmaIndexTab.MTM_QUICKEASY;
	}

	getValueMemory(): number {
		return Math.ceil((this.memorySize / this.maxMemorySize) * 100);
	}

	goToPermissionVoiceMenu() {
		this.router.navigate(['../../permissions'], { relativeTo: this.route });
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile() {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	actionLayout(position: string): string {
		return StandardMtmaLayout.actionLayout(this.screenWidth, position);
	}

	layoutMarginResponsive(): string {
		return StandardPageLayout.layoutMarginResponsive(this.screenWidth);
	}

	dynamicInputTextWidth(): string {
		return StandardPageLayout.dynamicInputTextWidth(this.screenWidth, 'w-25rem');
	}

	dynamicInputSearchWidth(): string {
		return StandardMtmaLayout.dynamicInputSearchWidth(this.screenWidth, 'w-25rem');
	}
}

